import { CLASS_NAME } from 'constants/class-names'
import { ATTR_NAME } from 'constants/attr-name'

export class NativeComponent {
    // eslint-disable-next-line no-magic-numbers
    private HEIGHT_THRESHOLD = 40

    private listPosition: number | null

    /**
     * Initialize the native ad component by registering the list position (where to put the ad in the target section)
     * and attaching a mutation observer to the element if the ad has not been rendered in the element yet.
     *
     * @param el
     * @param targetSection
     */
    public constructor(
        public el: HTMLElement,
        public targetSection: HTMLElement
    ) {
        this.observeElement()
        this.listPosition = Number(
            el.getAttribute(ATTR_NAME.native.listPosition)
        )
        this.repositionAd()
    }

    /**
     * If the ad has not been shown yet attach a mutation observer to check again when the element changes.
     * The height of the element is used to determine if the ad has been shown yet - if it is less than a threshold
     * that means the ad is not there.
     */
    private observeElement(): void {
        const elementHeight = this.el.offsetHeight
        if (!this.showAd(elementHeight)) {
            const mutationObserver = new MutationObserver(
                async (mutations, observer) => {
                    mutations.forEach((mutation) => {
                        const targetElementHeight = (mutation.target as HTMLElement)
                            .offsetHeight
                        // show the mutated element if the height is sufficient
                        if (this.showAd(targetElementHeight)) {
                            // disconnect the mutation observer once the ad is shown
                            observer.disconnect()
                        }
                    })
                }
            )

            // attach the element to the observer
            mutationObserver.observe(this.el, {
                childList: true,
                subtree: true
            })
        }
    }

    /**
     * Determine if the ad can be shown yet.
     *
     * @param elementHeight
     */
    private showAd(elementHeight: number): boolean {
        // check to see if the ad section is tall enough to display
        if (elementHeight > this.HEIGHT_THRESHOLD) {
            // apply the article card class so that the height of the article isn't applied if no ad is there
            this.el.classList.add(CLASS_NAME.common.articleCard)
            this.el.classList.remove(CLASS_NAME.common.visuallyHidden)
            return true
        }

        return false
    }

    /**
     * Insert the ad at the correct position in the target list or excluded if too short.
     */
    private repositionAd(): void {
        const listPosition = this.listPosition || 0
        const wrappedElem = document.createElement('li')
        wrappedElem.appendChild(this.el)

        if (listPosition < this.targetSection.children.length) {
            this.targetSection.insertBefore(
                wrappedElem,
                this.targetSection.children[listPosition]
            )
        } else if (listPosition === this.targetSection.children.length) {
            this.targetSection.appendChild(wrappedElem)
        }
    }
}
